import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../../components/CustomButton';
import { useCopySeasonMutation, usePreviewCopySeasonMutation } from '../../../../api/organization/organizationApiSlice';
import type { CopySeasonArgs, CopySeasonReport, Season } from '../../../../api/organization/allocationWindowTypes';
import { formatDay, isDay, windowErrorMessage } from '../../../organizations/allocationWindows/windowDraft';
import { WEEKDAY_KEYS } from '../../../organizations/allocationWindows/GrantWindowsEditor';

const inputClass = 'rounded-md border border-foreground/15 px-2 py-1.5 text-sm bg-text-contrast text-foreground '
    + 'focus:outline-none focus:border-foreground/40';

interface CopySeasonPanelProps {
    source: Season;
    groupIds: string[];
    organizationIds: string[];
    onClose: () => void;
}

/**
 * "Copy season" (D7, PO 2026-10-24 Q4 A, Q5 A): every window of the season
 * shown, into a new season the operator states. A preview first; the copy
 * itself is all or nothing — any overlap with another organisation stops it.
 * Releases are not copied. Windows that ran only part of the old season are
 * flagged, so someone looks at them afterwards.
 */
const CopySeasonPanel: React.FC<CopySeasonPanelProps> = ({ source, groupIds, organizationIds, onClose }) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en';
    const [target, setTarget] = useState<Season>({ from: '', to: '' });
    const [report, setReport] = useState<{ args: CopySeasonArgs, report: CopySeasonReport } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState<number | null>(null);
    const [preview, { isLoading: previewing }] = usePreviewCopySeasonMutation();
    const [copy, { isLoading: copying }] = useCopySeasonMutation();

    const args: CopySeasonArgs = { source, target, groupIds, organizationIds };
    const targetValid = isDay(target.from) && isDay(target.to) && target.from <= target.to;
    const current = report && JSON.stringify(report.args) === JSON.stringify(args) ? report.report : null;

    const runPreview = async () => {
        setError(null);
        setDone(null);
        try {
            setReport({ args, report: await preview(args).unwrap() });
        } catch (err) {
            setReport(null);
            setError(windowErrorMessage(err, t('allocation_windows.copy.error')));
        }
    };

    const runCopy = async () => {
        setError(null);
        try {
            const result = await copy(args).unwrap();
            setReport(null);
            setDone(result.windows.length);
        } catch (err) {
            setError(windowErrorMessage(err, t('allocation_windows.copy.error')));
        }
    };

    const line = (w: { weekday: number, startTime: string, endTime: string }) => t('allocation_windows.time', {
        weekday: t(`asset_detail.weekdays.${WEEKDAY_KEYS[w.weekday]}`), start: w.startTime, end: w.endTime,
    });

    return (
        <div className={'flex flex-col gap-3 rounded-[5px] bg-text-contrast shadow-sm px-5 py-4'}>
            <div>
                <p className={'text-sm font-medium text-foreground'}>{t('allocation_windows.copy.title')}</p>
                <p className={'text-sm text-foreground/60'}>
                    {t('allocation_windows.copy.hint', {
                        from: formatDay(source.from, language),
                        to: formatDay(source.to, language),
                    })}
                </p>
            </div>
            <div className={'flex flex-wrap gap-3'}>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.copy.target_from')}
                    <input
                        type={'date'}
                        className={inputClass}
                        value={target.from}
                        onChange={(event) => setTarget({ ...target, from: event.target.value })}
                    />
                </label>
                <label className={'flex flex-col gap-1 text-xs text-foreground/60'}>
                    {t('allocation_windows.copy.target_to')}
                    <input
                        type={'date'}
                        className={inputClass}
                        value={target.to}
                        onChange={(event) => setTarget({ ...target, to: event.target.value })}
                    />
                </label>
            </div>

            {current && (
                <div className={'flex flex-col gap-2 text-sm'}>
                    <p className={'text-foreground'}>{t('allocation_windows.copy.count', { total: current.windows.length })}</p>
                    {current.windows.some((w) => w.partial) && (
                        <div>
                            <p className={'font-medium text-foreground'}>{t('allocation_windows.copy.partial_title')}</p>
                            <ul className={'list-disc pl-5 text-foreground/80'}>
                                {current.windows.filter((w) => w.partial).map((w) => (
                                    <li key={w.sourceWindowId}>
                                        {t('allocation_windows.copy.partial_line', {
                                            name: w.organizationName || t('common.not_applicable'),
                                            room: w.groupName || t('common.not_applicable'),
                                            time: line(w),
                                            from: formatDay(w.sourceValidFrom, language),
                                            to: formatDay(w.sourceValidTo, language),
                                        })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {current.problems.length > 0 && (
                        <div role={'alert'}>
                            <p className={'font-medium text-foreground'}>{t('allocation_windows.copy.problems_title')}</p>
                            <ul className={'list-disc pl-5 text-foreground/80'}>
                                {current.problems.map((problem) => (
                                    <li key={problem.grantId}>
                                        {`${problem.organizationName || t('common.not_applicable')} · `
                                            + `${problem.groupName || t('common.not_applicable')}: ${problem.message}`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {current.bookingConflicts.length > 0 && (
                        <div>
                            <p className={'font-medium text-foreground'}>
                                {t('allocation_windows.editor.conflicts_title', { total: current.bookingConflicts.length })}
                            </p>
                            <p className={'text-foreground/60'}>{t('allocation_windows.editor.conflicts_hint')}</p>
                            <ul className={'list-disc pl-5 text-foreground/80'}>
                                {current.bookingConflicts.map((conflict) => (
                                    <li key={`${conflict.grantId}-${conflict.orderId}-${conflict.date}`}>
                                        {t('allocation_windows.copy.conflict_line', {
                                            number: conflict.bookingNumber || t('common.not_applicable'),
                                            date: formatDay(conflict.date, language),
                                        })}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {done !== null && (
                <p className={'text-sm text-foreground'}>{t('allocation_windows.copy.done', { total: done })}</p>
            )}
            {error && <p className={'text-sm text-foreground'}>{error}</p>}

            <div className={'flex flex-wrap gap-3'}>
                <CustomButton title={t('common.close')} isFilled={false} onClick={onClose} />
                <CustomButton
                    title={t('allocation_windows.copy.preview')}
                    isFilled={false}
                    loading={previewing}
                    disabled={!targetValid}
                    onClick={runPreview}
                />
                <CustomButton
                    title={t('allocation_windows.copy.confirm')}
                    loading={copying}
                    disabled={!current || current.windows.length === 0 || current.problems.length > 0}
                    onClick={runCopy}
                />
            </div>
        </div>
    );
};

export default CopySeasonPanel;
